import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Polyline } from '@react-google-maps/api';
import { getCurrentSession } from '../services/authService';
import { Button, Card, Field, Pill, Select, StatusBadge, TextInput } from '../ui/primitives';
import {
  IconArrowRight,
  IconCalendar,
  IconCar,
  IconChevronDown,
  IconCircleCheck,
  IconClock,
  IconDollar,
  IconDots,
  IconEdit,
  IconNavigate,
  IconPhone,
  IconPin,
  IconTrash,
  IconX,
} from '../ui/icons';
import { cx } from '../ui/cx';

const DEFAULT_CENTER = { lat: 41.5067, lng: -90.5151 };
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const DRIVER_POLL_INTERVAL = 10000;

function toCoords(obj) {
  if (!obj) return null;
  const lat = obj.latitude ?? obj.lat;
  const lng = obj.longitude ?? obj.lng ?? obj.lon;
  if (lat == null || lng == null) return null;
  const nlat = parseFloat(lat);
  const nlng = parseFloat(lng);
  if (Number.isNaN(nlat) || Number.isNaN(nlng)) return null;
  return { lat: nlat, lng: nlng };
}

function carMarkerIcon(color = '#4F46E5') {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="36" height="36" fill="${color}"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(36, 36),
    anchor: new window.google.maps.Point(18, 18),
  };
}

function dotMarkerIcon(fill) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28"><circle cx="12" cy="12" r="8" fill="${fill}" stroke="white" stroke-width="3"/></svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(28, 28),
    anchor: new window.google.maps.Point(14, 14),
  };
}

const PICKUP_ICON_FILL = '#059669';
const DROPOFF_ICON_FILL = '#E11D48';

function driverIdFor(b) {
  const t = (b?.trips && b.trips[0]) || null;
  return (
    b?.assignedDriverId ??
    b?.driverId ??
    b?.driver?.driverId ??
    b?.driver?.id ??
    t?.assignedDriverId ??
    t?.driverId ??
    null
  );
}

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';

const IN_PROGRESS = new Set(['EN_ROUTE', 'PICKED_UP', 'IN_PROGRESS', 'IN_TRANSIT', 'STARTED']);
const UPCOMING = new Set(['SCHEDULED', 'CONFIRMED', 'DRAFT', 'PENDING', 'ASSIGNED', 'ACCEPTED', 'CONVERTED']);
const PAST = new Set(['COMPLETED', 'DROPPED_OFF', 'CANCELLED', 'CANCELED', 'EXPIRED', 'DECLINED']);

function bucket(status) {
  const s = (status || '').toUpperCase();
  if (IN_PROGRESS.has(s)) return 'upcoming';
  if (UPCOMING.has(s)) return 'upcoming';
  if (PAST.has(s)) return 'past';
  return 'upcoming';
}

function isInProgress(status) {
  return IN_PROGRESS.has((status || '').toUpperCase());
}

function isCancelled(status) {
  const s = (status || '').toUpperCase();
  return s === 'CANCELLED' || s === 'CANCELED' || s === 'DECLINED' || s === 'EXPIRED';
}

function formatDate(dt) {
  if (!dt) return null;
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(dt) {
  if (!dt) return null;
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt;
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function splitDateTime(dt) {
  if (!dt) return { date: '', time: '' };
  const m = String(dt).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (m) return { date: m[1], time: m[2] };
  return { date: '', time: '' };
}

function combineDateTime(date, time) {
  if (!date || !time) return null;
  return `${date}T${time}:00`;
}

// LocalDateTime ISO from API → value accepted by <input type="datetime-local">,
// which requires YYYY-MM-DDTHH:MM (no seconds).
function toDatetimeLocal(dt) {
  if (!dt) return '';
  const m = String(dt).match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
  return m ? m[1] : '';
}

// Inverse: pad seconds so the API's LocalDateTime parser is happy (mirrors
// combineDateTime's `${date}T${time}:00` format).
function flightTimeForApi(s) {
  if (!s) return null;
  return s.length === 16 ? `${s}:00` : s;
}

function shortLocation(loc) {
  if (!loc) return '—';
  if (loc.airportCode) return `${loc.city || loc.airportCode} (${loc.airportCode})`;
  return [loc.city, loc.state].filter(Boolean).join(', ') || loc.addressLine1 || '—';
}

function firstTrip(b) {
  return (b?.trips && b.trips[0]) || null;
}

function driverLabel(b) {
  const t = firstTrip(b);
  return (
    b?.assignedDriverName ||
    b?.driver?.name ||
    b?.driver ||
    t?.driver?.name ||
    t?.driverName ||
    null
  );
}

function vehicleLabel(b) {
  const t = firstTrip(b);
  return (
    b?.vehicleClass ||
    b?.vehicleType ||
    t?.vehicleClass ||
    t?.vehicleType ||
    null
  );
}

function InvoiceBadge({ booking }) {
  const state = booking?.invoice?.state || booking?.invoiceStatus;
  const amount =
    booking?.invoice?.amount != null
      ? Number(booking.invoice.amount)
      : booking?.totalAmount != null
      ? Number(booking.totalAmount)
      : null;
  if (!state) return null;
  const s = state.toString().toLowerCase();
  if (s === 'sent' || s === 'pending' || s === 'outstanding') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200">
        <IconDollar size={10} /> Invoice sent{amount != null ? ` · $${amount.toFixed(2)}` : ''}
      </span>
    );
  }
  if (s === 'paid') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
        <IconCircleCheck size={10} /> Paid{amount != null ? ` · $${amount.toFixed(2)}` : ''}
      </span>
    );
  }
  return null;
}

function TrackingView({ booking, onClose, authSession }) {
  const t = firstTrip(booking);
  const driver = driverLabel(booking) || 'Driver';
  const from = t ? shortLocation(t.pickup) : '—';
  const to = t ? shortLocation(t.dropoff) : '—';
  const eta = booking?.etaMinutes != null ? `~${booking.etaMinutes} min` : '~12 min';
  const vehicle = vehicleLabel(booking) || 'Sedan';

  const driverId = driverIdFor(booking);
  const [driverLocation, setDriverLocation] = useState(null);
  const mapRef = useRef(null);
  const hasFittedRef = useRef(false);

  const { isLoaded: mapsLoaded, loadError: mapsLoadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  const pickupPos = toCoords(t?.pickup);
  const dropoffPos = toCoords(t?.dropoff);
  const driverPos = toCoords(driverLocation);

  useEffect(() => {
    if (!driverId) return undefined;
    let cancelled = false;
    const fetchLoc = async () => {
      try {
        let token = authSession?.idToken;
        try {
          const fresh = await getCurrentSession();
          token = fresh.idToken;
        } catch (e) { /* use existing */ }
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_BASE}/api/driver-location/all`, { headers });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const match = (Array.isArray(data) ? data : []).find(
          (d) => String(d.driverId) === String(driverId)
        );
        setDriverLocation(match || null);
      } catch (err) {
        console.warn('[tracking-view] driver location fetch failed', err);
      }
    };
    fetchLoc();
    const id = setInterval(fetchLoc, DRIVER_POLL_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [driverId, authSession]);

  useEffect(() => {
    if (!mapsLoaded || !mapRef.current) return;
    const points = [driverPos, pickupPos, dropoffPos].filter(Boolean);
    if (points.length === 0) return;
    if (points.length >= 2 && !hasFittedRef.current) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend(p));
      mapRef.current.fitBounds(bounds, 60);
      hasFittedRef.current = true;
      return;
    }
    if (driverPos) mapRef.current.panTo(driverPos);
  }, [mapsLoaded, driverPos?.lat, driverPos?.lng, pickupPos?.lat, pickupPos?.lng, dropoffPos?.lat, dropoffPos?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const mapCenter = driverPos || pickupPos || dropoffPos || DEFAULT_CENTER;
  const hasAnyCoords = !!(driverPos || pickupPos || dropoffPos);

  const renderMap = () => {
    if (mapsLoadError) {
      return (
        <div className="flex items-center justify-center h-full text-sm text-rose-700 bg-slate-50 px-4 text-center">
          Failed to load Google Maps.
        </div>
      );
    }
    if (!mapsLoaded) {
      return (
        <div className="flex items-center justify-center h-full text-sm text-slate-500 bg-slate-50">
          Loading map…
        </div>
      );
    }
    if (!hasAnyCoords) {
      return (
        <div className="flex items-center justify-center h-full text-sm text-slate-500 bg-slate-50 px-4 text-center">
          No coordinates available for this trip yet.
        </div>
      );
    }
    return (
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={mapCenter}
        zoom={12}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
        onLoad={(map) => { mapRef.current = map; }}
      >
        {pickupPos && (
          <Marker
            position={pickupPos}
            icon={dotMarkerIcon(PICKUP_ICON_FILL)}
            title={`Pickup · ${from}`}
          />
        )}
        {dropoffPos && (
          <Marker
            position={dropoffPos}
            icon={dotMarkerIcon(DROPOFF_ICON_FILL)}
            title={`Dropoff · ${to}`}
          />
        )}
        {pickupPos && dropoffPos && (
          <Polyline
            path={[pickupPos, dropoffPos]}
            options={{ strokeColor: '#4F46E5', strokeOpacity: 0.7, strokeWeight: 3, geodesic: true }}
          />
        )}
        {driverPos && (
          <Marker
            position={driverPos}
            icon={carMarkerIcon('#4F46E5')}
            title={`${driver} · ETA ${eta}`}
          />
        )}
      </GoogleMap>
    );
  };

  return (
    <div className="mt-4 rounded-xl border border-indigo-200 overflow-hidden fade-in bg-white">
      <div className="relative h-64">
        {renderMap()}
        {!driverPos && mapsLoaded && hasAnyCoords && (
          <div className="absolute top-3 left-3 bg-white/95 rounded-lg px-2.5 py-1 text-xs shadow-sm border border-slate-200 z-10 inline-flex items-center gap-1.5 text-slate-600">
            <span className="relative flex items-center justify-center w-2 h-2">
              <span className="absolute w-3 h-3 rounded-full bg-slate-300/60 animate-ping" />
              <span className="relative w-2 h-2 rounded-full bg-slate-400" />
            </span>
            Waiting for driver location
          </div>
        )}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-white/95 border border-slate-200 text-slate-600 cursor-pointer
            hover:bg-white shadow-sm flex items-center justify-center z-10
            transition-all duration-150
            focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          aria-label="Close tracking"
        >
          <IconX size={14} />
        </button>
      </div>

      <div className="p-5 border-t border-slate-100">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                <span className="relative flex items-center justify-center w-2 h-2">
                  <span className="absolute w-3 h-3 rounded-full bg-emerald-400/50 animate-ping" />
                  <span className="relative w-2 h-2 rounded-full bg-emerald-500" />
                </span>
                Live tracking
              </span>
            </div>
            <div className="mt-1 text-lg font-bold text-slate-900">Arriving in {eta}</div>
            <div className="text-sm text-slate-600 mt-0.5">
              {driver} · {vehicle}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Trip</div>
            <div className="text-sm font-semibold text-slate-800 mt-0.5">
              {from} → {to}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100">
          <Button variant="outlineIndigo" size="md" onClick={() => alert('Call driver coming soon')}>
            <IconPhone size={14} /> Call driver
          </Button>
          <div className="ml-auto text-xs text-slate-400 italic">Map is a live-tracking preview.</div>
        </div>
      </div>
    </div>
  );
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

const SERVICE_CITIES = [
  'Quad Cities', 'Chicago', 'Des Moines', 'Waterloo', 'Cedar Rapids',
  'Dubuque', 'Wisconsin Dells', 'Galesburg', 'Williamsburg', 'Urbana',
  'Ottumwa', 'Galena', 'Iowa City', 'Beaver Dam', 'Other',
];

const CITY_STATE_MAP = {
  'Quad Cities': 'IL',
  'Chicago': 'IL',
  'Des Moines': 'IA',
  'Waterloo': 'IA',
  'Cedar Rapids': 'IA',
  'Dubuque': 'IA',
  'Wisconsin Dells': 'WI',
  'Galesburg': 'IL',
  'Williamsburg': 'IA',
  'Urbana': 'IL',
  'Ottumwa': 'IA',
  'Galena': 'IL',
  'Iowa City': 'IA',
  'Beaver Dam': 'WI',
};

// Chicago is the only airport city in the service area. Picking Chicago
// auto-enables the airport toggle and seeds ORD; the user can still switch
// to MDW or uncheck the toggle afterwards.
const AIRPORT_CITIES = { 'Chicago': 'ORD' };

// Known US airport code → expected city (lowercased). Mirrors the server-side
// validation list in BookingService.java so the modal clears a mismatched code
// before the user submits — otherwise the API throws a 400 on save.
const KNOWN_AIRPORT_CITY = {
  ORD: 'chicago', MDW: 'chicago', DSM: 'des moines', CID: 'cedar rapids',
  MLI: 'moline', JFK: 'new york', LGA: 'new york', EWR: 'newark',
  LAX: 'los angeles', SFO: 'san francisco', SJC: 'san jose', ATL: 'atlanta',
  DFW: 'dallas', DAL: 'dallas', MIA: 'miami', BOS: 'boston', SEA: 'seattle',
  LAS: 'las vegas', DEN: 'denver', PHX: 'phoenix', IAH: 'houston',
  HOU: 'houston', MSP: 'minneapolis', DTW: 'detroit', PHL: 'philadelphia',
  BWI: 'baltimore', DCA: 'washington', IAD: 'washington',
};

function airportCityMismatch(airportCode, city) {
  if (!airportCode || !city) return false;
  const expected = KNOWN_AIRPORT_CITY[airportCode.trim().toUpperCase()];
  if (!expected) return false;
  return expected !== city.trim().toLowerCase();
}

// Map a stored city string to a (choice, custom) pair for the dropdown:
// known SERVICE_CITIES entries become the choice; anything else falls into
// "Other" with the original text preserved in the custom field.
function cityToChoice(city) {
  const v = (city || '').trim();
  if (!v) return { choice: '', custom: '' };
  const match = SERVICE_CITIES.find(
    (c) => c !== 'Other' && c.toLowerCase() === v.toLowerCase()
  );
  return match ? { choice: match, custom: '' } : { choice: 'Other', custom: v };
}

function resolveCity(choice, custom) {
  if (!choice) return '';
  if (choice === 'Other') return (custom || '').trim();
  return choice;
}

function sameCity(a, b) {
  const x = (a || '').trim().toLowerCase();
  const y = (b || '').trim().toLowerCase();
  if (!x || !y) return false;
  return x === y;
}

function tripFormFromTrip(t) {
  const { date, time } = splitDateTime(t?.pickup?.datetime);
  const pickupAirportCode = t?.pickup?.airportCode || '';
  const dropoffAirportCode = t?.dropoff?.airportCode || '';
  const pickupCityParts = cityToChoice(t?.pickup?.city);
  const dropoffCityParts = cityToChoice(t?.dropoff?.city);
  return {
    bookingTripId: t?.bookingTripId ?? null,
    pickupAddressLine1: t?.pickup?.addressLine1 || '',
    pickupCityChoice: pickupCityParts.choice,
    pickupCityCustom: pickupCityParts.custom,
    pickupState: t?.pickup?.state || '',
    pickupAirportCode,
    pickupIsAirport: !!t?.pickup?.isAirport || !!pickupAirportCode,
    pickupAirline: t?.pickup?.airline || '',
    pickupFlightNumber: t?.pickup?.flightNumber || '',
    pickupTerminal: t?.pickup?.terminal || '',
    pickupFlightTime: toDatetimeLocal(t?.pickup?.flightTime),
    pickupDate: date,
    pickupTime: time,
    dropoffAddressLine1: t?.dropoff?.addressLine1 || '',
    dropoffCityChoice: dropoffCityParts.choice,
    dropoffCityCustom: dropoffCityParts.custom,
    dropoffState: t?.dropoff?.state || '',
    dropoffAirportCode,
    dropoffIsAirport: !!t?.dropoff?.isAirport || !!dropoffAirportCode,
    dropoffAirline: t?.dropoff?.airline || '',
    dropoffFlightNumber: t?.dropoff?.flightNumber || '',
    dropoffTerminal: t?.dropoff?.terminal || '',
    dropoffFlightTime: toDatetimeLocal(t?.dropoff?.flightTime),
    numTravelers: t?.travelers?.numTravelers ?? 1,
    numBags: t?.travelers?.numBags ?? 0,
    numHandBags: t?.travelers?.numHandBags ?? 0,
    numChildSeats: t?.travelers?.numChildSeats ?? 0,
  };
}

function tripSummary(form, idx) {
  const pickupCity = resolveCity(form.pickupCityChoice, form.pickupCityCustom);
  const dropoffCity = resolveCity(form.dropoffCityChoice, form.dropoffCityCustom);
  const from = form.pickupAirportCode || pickupCity || `Trip ${idx + 1}`;
  const to = form.dropoffAirportCode || dropoffCity || '?';
  return `${from} → ${to}`;
}

function toIntOrDefault(value, fallback) {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? fallback : n;
}

function EditBookingModal({ booking, onClose, onSave, saving }) {
  const tripsInput = useMemo(
    () => (Array.isArray(booking?.trips) && booking.trips.length > 0 ? booking.trips : [null]),
    [booking]
  );
  const [tripForms, setTripForms] = useState(() => tripsInput.map(tripFormFromTrip));
  const [expanded, setExpanded] = useState(() => tripsInput.map((_, i) => i === 0));
  const [specialInstructions, setSpecialInstructions] = useState(booking?.specialInstructions || '');
  const [error, setError] = useState(null);

  const updateTrip = (idx, patch) => {
    // Any field change clears the stale server-side error so the user knows
    // the prior validation message no longer applies.
    setError(null);
    setTripForms((forms) => forms.map((f, i) => {
      if (i !== idx) return f;
      let next = { ...f, ...patch };
      // Toggling 'This is an airport' swaps which field is in play and clears
      // the other side so we never submit a payload with both a street address
      // and an airport code on the same location.
      if ('pickupIsAirport' in patch) {
        next = next.pickupIsAirport
          ? { ...next, pickupAddressLine1: '' }
          : {
              ...next,
              pickupAirportCode: null,
              pickupAirline: '',
              pickupFlightNumber: '',
              pickupTerminal: '',
              pickupFlightTime: '',
            };
      }
      if ('dropoffIsAirport' in patch) {
        next = next.dropoffIsAirport
          ? { ...next, dropoffAddressLine1: '' }
          : {
              ...next,
              dropoffAirportCode: null,
              dropoffAirline: '',
              dropoffFlightNumber: '',
              dropoffTerminal: '',
              dropoffFlightTime: '',
            };
      }
      // City dropdown change: auto-set state from CITY_STATE_MAP (except for
      // 'Other'), and auto-toggle airport + seed code for Chicago (or clear
      // them when switching to a non-airport service city). 'Other' leaves
      // airport/state alone so the user can choose manually.
      if ('pickupCityChoice' in patch) {
        const choice = next.pickupCityChoice;
        if (choice && choice !== 'Other') {
          if (CITY_STATE_MAP[choice]) next = { ...next, pickupState: CITY_STATE_MAP[choice] };
          if (AIRPORT_CITIES[choice]) {
            next = { ...next, pickupIsAirport: true, pickupAirportCode: AIRPORT_CITIES[choice], pickupAddressLine1: '' };
          } else {
            next = { ...next, pickupIsAirport: false, pickupAirportCode: '' };
          }
        }
        // Clear the custom field when leaving 'Other' so a stale value doesn't
        // resurface if the user toggles back later.
        if (choice !== 'Other') next = { ...next, pickupCityCustom: '' };
      }
      if ('dropoffCityChoice' in patch) {
        const choice = next.dropoffCityChoice;
        if (choice && choice !== 'Other') {
          if (CITY_STATE_MAP[choice]) next = { ...next, dropoffState: CITY_STATE_MAP[choice] };
          if (AIRPORT_CITIES[choice]) {
            next = { ...next, dropoffIsAirport: true, dropoffAirportCode: AIRPORT_CITIES[choice], dropoffAddressLine1: '' };
          } else {
            next = { ...next, dropoffIsAirport: false, dropoffAirportCode: '' };
          }
        }
        if (choice !== 'Other') next = { ...next, dropoffCityCustom: '' };
      }
      // Custom 'Other' city text — fall back to the legacy mismatch check so
      // a typed city like 'Des Moines' clears a stale 'ORD' airport code.
      if ('pickupCityCustom' in patch) {
        const resolved = resolveCity(next.pickupCityChoice, next.pickupCityCustom);
        if (airportCityMismatch(next.pickupAirportCode, resolved)) {
          next = { ...next, pickupAirportCode: '' };
        }
      }
      if ('dropoffCityCustom' in patch) {
        const resolved = resolveCity(next.dropoffCityChoice, next.dropoffCityCustom);
        if (airportCityMismatch(next.dropoffAirportCode, resolved)) {
          next = { ...next, dropoffAirportCode: '' };
        }
      }
      return next;
    }));
  };

  // Per-trip same-city flag drives the inline error message and the Save-button
  // disabled state. Empty values on either side don't count as a conflict.
  const sameCityFlags = useMemo(
    () => tripForms.map((f) => sameCity(
      resolveCity(f.pickupCityChoice, f.pickupCityCustom),
      resolveCity(f.dropoffCityChoice, f.dropoffCityCustom),
    )),
    [tripForms]
  );
  const hasSameCityError = sameCityFlags.some(Boolean);
  const toggleExpanded = (idx) => {
    setExpanded((arr) => arr.map((v, i) => (i === idx ? !v : v)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    for (let i = 0; i < tripForms.length; i++) {
      const f = tripForms[i];
      if (!combineDateTime(f.pickupDate, f.pickupTime)) {
        setError(`Trip ${i + 1}: please provide both a pickup date and time.`);
        setExpanded((arr) => arr.map((v, j) => (j === i ? true : v)));
        return;
      }
      const travelers = toIntOrDefault(f.numTravelers, 0);
      if (travelers < 1) {
        setError(`Trip ${i + 1}: number of travelers must be at least 1.`);
        setExpanded((arr) => arr.map((v, j) => (j === i ? true : v)));
        return;
      }
      if (sameCityFlags[i]) {
        setError(`Trip ${i + 1}: pickup and dropoff cannot be the same city.`);
        setExpanded((arr) => arr.map((v, j) => (j === i ? true : v)));
        return;
      }
    }
    const payload = {
      specialInstructions,
      trips: tripForms.map((f) => {
        const pickupIsAirport = !!f.pickupIsAirport;
        const dropoffIsAirport = !!f.dropoffIsAirport;
        const pickupCity = resolveCity(f.pickupCityChoice, f.pickupCityCustom);
        const dropoffCity = resolveCity(f.dropoffCityChoice, f.dropoffCityCustom);
        return {
          bookingTripId: f.bookingTripId,
          pickup: {
            addressLine1: f.pickupAddressLine1 || null,
            city: pickupCity || null,
            state: f.pickupState || null,
            airportCode: pickupIsAirport ? (f.pickupAirportCode || null) : null,
            isAirport: pickupIsAirport,
            // Backend treats null as "leave unchanged" and "" as "set to empty"
            // for these fields (see BookingService.updateTrip pickup block).
            // When the airport toggle is off we send "" to clear any stale
            // value; flightTime is LocalDateTime so we send null (the only
            // field this PATCH-style endpoint can't wipe through this path).
            airline: pickupIsAirport ? f.pickupAirline : '',
            flightNumber: pickupIsAirport ? f.pickupFlightNumber : '',
            terminal: pickupIsAirport ? f.pickupTerminal : '',
            flightTime: pickupIsAirport ? flightTimeForApi(f.pickupFlightTime) : null,
            datetime: combineDateTime(f.pickupDate, f.pickupTime),
          },
          dropoff: {
            addressLine1: f.dropoffAddressLine1 || null,
            city: dropoffCity || null,
            state: f.dropoffState || null,
            airportCode: dropoffIsAirport ? (f.dropoffAirportCode || null) : null,
            isAirport: dropoffIsAirport,
            airline: dropoffIsAirport ? f.dropoffAirline : '',
            flightNumber: dropoffIsAirport ? f.dropoffFlightNumber : '',
            terminal: dropoffIsAirport ? f.dropoffTerminal : '',
            flightTime: dropoffIsAirport ? flightTimeForApi(f.dropoffFlightTime) : null,
          },
          travelers: {
            numTravelers: toIntOrDefault(f.numTravelers, 1),
            numBags: toIntOrDefault(f.numBags, 0),
            numHandBags: toIntOrDefault(f.numHandBags, 0),
            numChildSeats: toIntOrDefault(f.numChildSeats, 0),
          },
        };
      }),
    };
    try {
      await onSave(payload);
    } catch (err) {
      setError(err.message || 'Failed to save changes');
    }
  };

  const isMulti = tripForms.length > 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm fade-in"
      onClick={() => { if (!saving) onClose(); }}
      role="presentation"
    >
      <div
        className="relative bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-booking-title"
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-slate-100 shrink-0">
          <div>
            <h2 id="edit-booking-title" className="text-lg font-bold text-slate-900">Edit booking</h2>
            <p className="mono text-xs text-slate-500 mt-0.5">{booking.bookingNumber || booking.bookingId}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700
              flex items-center justify-center cursor-pointer transition-all duration-150
              focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <IconX size={14} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
            {tripForms.map((form, idx) => (
              <TripSection
                key={form.bookingTripId ?? idx}
                idx={idx}
                form={form}
                onChange={(patch) => updateTrip(idx, patch)}
                expanded={expanded[idx]}
                onToggle={() => toggleExpanded(idx)}
                saving={saving}
                showHeader={isMulti}
                sameCityError={sameCityFlags[idx]}
              />
            ))}

            <div className="rounded-xl border border-slate-200 p-4 space-y-3">
              <h3 className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Notes</h3>
              <Field label="Special instructions">
                <textarea
                  value={specialInstructions}
                  onChange={(e) => { setSpecialInstructions(e.target.value); setError(null); }}
                  disabled={saving}
                  rows={3}
                  placeholder="Anything the driver should know?"
                  className="w-full rounded-lg border border-slate-300 bg-white text-sm text-slate-900 placeholder:text-slate-400
                    px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                    transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                />
              </Field>
            </div>

            {error && (
              <div className="rounded-lg px-3 py-2 text-sm bg-rose-50 border border-rose-200 text-rose-800">
                {error}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100 shrink-0 bg-white rounded-b-xl">
            <p className="mr-auto text-xs text-slate-500 italic">Price is recalculated on save.</p>
            <Button type="button" variant="outline" size="md" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="md" disabled={saving || hasSameCityError}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TripSection({ idx, form, onChange, expanded, onToggle, saving, showHeader, sameCityError }) {
  const body = (
    <div className="space-y-4 pt-1">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h4 className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Pickup</h4>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!form.pickupIsAirport}
              onChange={(e) => onChange({ pickupIsAirport: e.target.checked })}
              disabled={saving}
              className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
            />
            This is an airport pickup
          </label>
        </div>
        <Field label="Street address">
          <TextInput
            value={form.pickupAddressLine1}
            onChange={(e) => onChange({ pickupAddressLine1: e.target.value })}
            disabled={saving || form.pickupIsAirport}
            placeholder={form.pickupIsAirport ? 'Not used for airport pickups' : '123 Main St'}
          />
        </Field>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="City">
            <Select
              value={form.pickupCityChoice}
              onChange={(e) => onChange({ pickupCityChoice: e.target.value })}
              disabled={saving}
            >
              <option value="">—</option>
              {SERVICE_CITIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </Select>
          </Field>
          <Field label="State">
            <Select
              value={form.pickupState}
              onChange={(e) => onChange({ pickupState: e.target.value })}
              disabled={saving}
            >
              <option value="">—</option>
              {US_STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
            </Select>
          </Field>
          <Field label="Airport code">
            <TextInput
              value={form.pickupAirportCode || ''}
              onChange={(e) => onChange({ pickupAirportCode: e.target.value.toUpperCase() })}
              disabled={saving || !form.pickupIsAirport}
              maxLength={3}
              placeholder={form.pickupIsAirport ? 'ORD' : 'Toggle airport on'}
            />
          </Field>
        </div>
        {form.pickupCityChoice === 'Other' && (
          <Field label="Pickup city (custom)">
            <TextInput
              value={form.pickupCityCustom}
              onChange={(e) => onChange({ pickupCityCustom: e.target.value })}
              disabled={saving}
              placeholder="Type city name"
            />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Pickup date">
            <TextInput
              type="date"
              value={form.pickupDate}
              onChange={(e) => onChange({ pickupDate: e.target.value })}
              disabled={saving}
              required
            />
          </Field>
          <Field label="Pickup time">
            <TextInput
              type="time"
              value={form.pickupTime}
              onChange={(e) => onChange({ pickupTime: e.target.value })}
              disabled={saving}
              required
            />
          </Field>
        </div>
        {form.pickupIsAirport && (
          <div className="grid grid-cols-2 gap-3 pt-1">
            <Field label="Airline">
              <TextInput
                value={form.pickupAirline}
                onChange={(e) => onChange({ pickupAirline: e.target.value })}
                disabled={saving}
                placeholder="United"
              />
            </Field>
            <Field label="Flight #">
              <TextInput
                value={form.pickupFlightNumber}
                onChange={(e) => onChange({ pickupFlightNumber: e.target.value })}
                disabled={saving}
                placeholder="UA123"
              />
            </Field>
            <Field label="Terminal">
              <TextInput
                value={form.pickupTerminal}
                onChange={(e) => onChange({ pickupTerminal: e.target.value })}
                disabled={saving}
                placeholder="5"
              />
            </Field>
            <Field label="Arrival time">
              <TextInput
                type="datetime-local"
                value={form.pickupFlightTime}
                onChange={(e) => onChange({ pickupFlightTime: e.target.value })}
                disabled={saving}
              />
            </Field>
          </div>
        )}
      </div>

      <div className="space-y-3 pt-2 border-t border-slate-100">
        <div className="flex items-center justify-between gap-3 pt-2">
          <h4 className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Dropoff</h4>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!form.dropoffIsAirport}
              onChange={(e) => onChange({ dropoffIsAirport: e.target.checked })}
              disabled={saving}
              className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
            />
            This is an airport dropoff
          </label>
        </div>
        <Field label="Street address">
          <TextInput
            value={form.dropoffAddressLine1}
            onChange={(e) => onChange({ dropoffAddressLine1: e.target.value })}
            disabled={saving || form.dropoffIsAirport}
            placeholder={form.dropoffIsAirport ? 'Not used for airport dropoffs' : '123 Main St'}
          />
        </Field>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="City">
            <Select
              value={form.dropoffCityChoice}
              onChange={(e) => onChange({ dropoffCityChoice: e.target.value })}
              disabled={saving}
            >
              <option value="">—</option>
              {SERVICE_CITIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </Select>
          </Field>
          <Field label="State">
            <Select
              value={form.dropoffState}
              onChange={(e) => onChange({ dropoffState: e.target.value })}
              disabled={saving}
            >
              <option value="">—</option>
              {US_STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
            </Select>
          </Field>
          <Field label="Airport code">
            <TextInput
              value={form.dropoffAirportCode || ''}
              onChange={(e) => onChange({ dropoffAirportCode: e.target.value.toUpperCase() })}
              disabled={saving || !form.dropoffIsAirport}
              maxLength={3}
              placeholder={form.dropoffIsAirport ? 'ORD' : 'Toggle airport on'}
            />
          </Field>
        </div>
        {form.dropoffCityChoice === 'Other' && (
          <Field label="Dropoff city (custom)">
            <TextInput
              value={form.dropoffCityCustom}
              onChange={(e) => onChange({ dropoffCityCustom: e.target.value })}
              disabled={saving}
              placeholder="Type city name"
            />
          </Field>
        )}
        {form.dropoffIsAirport && (
          <div className="grid grid-cols-2 gap-3 pt-1">
            <Field label="Airline">
              <TextInput
                value={form.dropoffAirline}
                onChange={(e) => onChange({ dropoffAirline: e.target.value })}
                disabled={saving}
                placeholder="United"
              />
            </Field>
            <Field label="Flight #">
              <TextInput
                value={form.dropoffFlightNumber}
                onChange={(e) => onChange({ dropoffFlightNumber: e.target.value })}
                disabled={saving}
                placeholder="UA123"
              />
            </Field>
            <Field label="Terminal">
              <TextInput
                value={form.dropoffTerminal}
                onChange={(e) => onChange({ dropoffTerminal: e.target.value })}
                disabled={saving}
                placeholder="5"
              />
            </Field>
            <Field label="Departure time">
              <TextInput
                type="datetime-local"
                value={form.dropoffFlightTime}
                onChange={(e) => onChange({ dropoffFlightTime: e.target.value })}
                disabled={saving}
              />
            </Field>
          </div>
        )}
        {sameCityError && (
          <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2.5 py-1.5">
            Pickup and dropoff cannot be the same city.
          </div>
        )}
      </div>

      <div className="space-y-3 pt-2 border-t border-slate-100">
        <h4 className="text-xs uppercase font-semibold text-slate-500 tracking-wider pt-2">Travelers</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Travelers">
            <TextInput
              type="number"
              min={1}
              value={form.numTravelers}
              onChange={(e) => onChange({ numTravelers: e.target.value })}
              disabled={saving}
              required
            />
          </Field>
          <Field label="Bags">
            <TextInput
              type="number"
              min={0}
              value={form.numBags}
              onChange={(e) => onChange({ numBags: e.target.value })}
              disabled={saving}
            />
          </Field>
          <Field label="Hand bags">
            <TextInput
              type="number"
              min={0}
              value={form.numHandBags}
              onChange={(e) => onChange({ numHandBags: e.target.value })}
              disabled={saving}
            />
          </Field>
          <Field label="Child seats">
            <TextInput
              type="number"
              min={0}
              value={form.numChildSeats}
              onChange={(e) => onChange({ numChildSeats: e.target.value })}
              disabled={saving}
            />
          </Field>
        </div>
      </div>
    </div>
  );

  if (!showHeader) {
    return <div className="rounded-xl border border-slate-200 p-4">{body}</div>;
  }

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        disabled={saving}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100
          text-left cursor-pointer transition-all duration-150
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-inset
          disabled:opacity-50 disabled:cursor-not-allowed"
        aria-expanded={expanded}
      >
        <span className="text-sm font-semibold text-slate-900">
          Trip {idx + 1}: <span className="font-normal text-slate-600">{tripSummary(form, idx)}</span>
        </span>
        <IconChevronDown
          size={14}
          className={cx('text-slate-400 transition-transform duration-150', !expanded && '-rotate-90')}
        />
      </button>
      {expanded && <div className="p-4 border-t border-slate-100">{body}</div>}
    </div>
  );
}

function BookingRow({ booking, trackingOpen, setTrackingOpen, menuOpen, setMenuOpen, authSession, onCancelBooking, cancelling, onEditBooking }) {
  const trip = firstTrip(booking);
  const status = booking.status || trip?.tripStatus || '—';
  const inProgress = isInProgress(status);
  const cancelled = isCancelled(status);
  const tracking = trackingOpen === booking.bookingId && inProgress;
  const menu = menuOpen === booking.bookingId;
  const canCancel = (status || '').toUpperCase() === 'CONFIRMED';
  const canEdit = (status || '').toUpperCase() === 'CONFIRMED';

  const from = trip ? shortLocation(trip.pickup) : '—';
  const to = trip ? shortLocation(trip.dropoff) : '—';
  const dt = trip?.pickup?.datetime;
  const date = formatDate(dt);
  const time = formatTime(dt);
  const travelers = trip?.travelers?.numTravelers;
  const bags = trip?.travelers?.numBags;
  const driver = driverLabel(booking);
  const vehicle = vehicleLabel(booking);
  const id = booking.bookingNumber || booking.bookingId;

  return (
    <Card
      className={cx(
        'p-5 transition-all hover:border-slate-300 relative',
        tracking && 'ring-2 ring-indigo-200 border-indigo-300'
      )}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
          <IconCar size={20} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="mono text-sm font-semibold text-slate-900">{id}</span>
            <StatusBadge status={status} />
            {driver && !cancelled && (
              <span className="text-xs text-slate-500">
                · Driver <span className="font-medium text-slate-700">{driver}</span>
              </span>
            )}
            {!driver && !cancelled && bucket(status) === 'upcoming' && !inProgress && (
              <span className="text-xs text-slate-400 italic">· Awaiting driver assignment</span>
            )}
            <InvoiceBadge booking={booking} />
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="inline-flex items-center gap-1.5 text-slate-800 font-medium min-w-0">
              <IconPin size={13} className="text-slate-400 shrink-0" />
              <span className="truncate">{from}</span>
            </span>
            <IconArrowRight size={13} className="text-slate-300 shrink-0" />
            <span className="inline-flex items-center gap-1.5 text-slate-800 font-medium min-w-0">
              <IconPin size={13} className="text-slate-400 shrink-0" />
              <span className="truncate">{to}</span>
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-2">
            {date && (
              <span className="inline-flex items-center gap-1">
                <IconCalendar size={12} /> {date}
              </span>
            )}
            {time && (
              <span className="inline-flex items-center gap-1">
                <IconClock size={12} /> {time}
              </span>
            )}
            {travelers != null && (
              <span>
                {travelers} passenger{travelers === 1 ? '' : 's'}
                {bags != null && ` · ${bags} bag${bags === 1 ? '' : 's'}`}
              </span>
            )}
            {vehicle && (
              <span className="inline-flex items-center gap-1">
                <IconCar size={12} /> {vehicle}
              </span>
            )}
          </div>

          {booking.totalAmount != null && (
            <div className="mt-2 text-xs text-slate-500">
              Total{' '}
              <span className="mono font-medium text-slate-700">
                ${Number(booking.totalAmount).toFixed(2)} {booking.currencyCode || 'USD'}
              </span>
            </div>
          )}

          {cancelled && (booking.cancelReason || booking.cancellationReason) && (
            <div className="mt-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5">
              {booking.cancelReason || booking.cancellationReason}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {inProgress && (
            <Button
              variant={tracking ? 'primary' : 'outlineIndigo'}
              size="sm"
              onClick={() => setTrackingOpen(tracking ? null : booking.bookingId)}
            >
              <IconNavigate size={13} /> {tracking ? 'Hide tracking' : 'Track driver'}
            </Button>
          )}
          <div className="relative">
            <button
              onClick={() => setMenuOpen(menu ? null : booking.bookingId)}
              className="w-9 h-9 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center"
              aria-label="More options"
            >
              <IconDots size={16} />
            </button>
            {menu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(null)} />
                <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl border border-slate-200 shadow-lg p-1 z-20 fade-in">
                  {canEdit && (
                    <button
                      className="w-full text-left px-3 py-2 text-sm rounded-lg flex items-center gap-2 text-slate-700 cursor-pointer
                        hover:bg-slate-50 transition-all duration-150
                        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-inset"
                      onClick={() => onEditBooking(booking)}
                    >
                      <IconEdit size={13} /> Edit
                    </button>
                  )}
                  {canCancel && (
                    <button
                      disabled={cancelling}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg flex items-center gap-2 text-rose-600 cursor-pointer
                        hover:bg-rose-50 transition-all duration-150
                        focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-inset
                        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      onClick={() => onCancelBooking(booking)}
                    >
                      <IconTrash size={13} /> {cancelling ? 'Cancelling…' : 'Cancel booking'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {tracking && (
        <TrackingView booking={booking} onClose={() => setTrackingOpen(null)} authSession={authSession} />
      )}
    </Card>
  );
}

function MyRidesPage({ authSession }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [trackingOpen, setTrackingOpen] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [flash, setFlash] = useState(null);
  const flashTimerRef = useRef(null);

  const userId = authSession?.user?.sub;

  const fetchBookings = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      let token = authSession?.idToken;
      try {
        const freshSession = await getCurrentSession();
        token = freshSession.idToken;
      } catch (e) { /* use existing */ }

      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/api/bookings/by-user/${userId}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBookings(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      setError('Failed to load your rides');
    } finally {
      setLoading(false);
    }
  }, [userId, authSession]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const showFlash = useCallback((type, message) => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlash({ type, message });
    flashTimerRef.current = setTimeout(() => setFlash(null), 4000);
  }, []);

  useEffect(() => () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  const cancelBooking = useCallback(async (booking) => {
    const bookingNumber = booking?.bookingNumber;
    if (!bookingNumber) {
      showFlash('error', 'Booking is missing a booking number.');
      return;
    }
    const ok = window.confirm(
      `Are you sure you want to cancel booking ${bookingNumber}? This cannot be undone.`
    );
    if (!ok) return;
    setCancellingId(booking.bookingId);
    try {
      let token = authSession?.idToken;
      try {
        const fresh = await getCurrentSession();
        token = fresh.idToken;
      } catch (e) { /* use existing */ }
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_BASE}/api/bookings/cancel/${bookingNumber}`, {
        method: 'PUT',
        headers,
      });
      if (!res.ok) {
        let detail = '';
        try { detail = await res.text(); } catch (e) { /* ignore */ }
        throw new Error(detail || `HTTP ${res.status}`);
      }
      showFlash('success', `Booking ${bookingNumber} cancelled.`);
      await fetchBookings();
    } catch (err) {
      console.error('Cancel booking failed:', err);
      showFlash('error', `Failed to cancel booking: ${err.message || 'Unknown error'}`);
    } finally {
      setCancellingId(null);
      setMenuOpen(null);
    }
  }, [authSession, fetchBookings, showFlash]);

  const openEdit = useCallback((booking) => {
    setMenuOpen(null);
    setEditingBooking(booking);
  }, []);

  const closeEdit = useCallback(() => {
    setEditingBooking((current) => (savingEdit ? current : null));
  }, [savingEdit]);

  const saveEdit = useCallback(async (payload) => {
    const bookingNumber = editingBooking?.bookingNumber;
    if (!bookingNumber) {
      throw new Error('Booking is missing a booking number.');
    }
    setSavingEdit(true);
    try {
      let token = authSession?.idToken;
      try {
        const fresh = await getCurrentSession();
        token = fresh.idToken;
      } catch (e) { /* use existing */ }
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_BASE}/api/bookings/by-number/${bookingNumber}/with-trips`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let detail = '';
        try { detail = await res.text(); } catch (e) { /* ignore */ }
        throw new Error(detail || `HTTP ${res.status}`);
      }
      let newTotal = null;
      try {
        const body = await res.json();
        if (body?.totalAmount != null) newTotal = Number(body.totalAmount);
      } catch (e) { /* response not JSON */ }
      setEditingBooking(null);
      const totalSuffix = newTotal != null && !Number.isNaN(newTotal)
        ? ` New total: $${newTotal.toFixed(2)}`
        : '';
      showFlash('success', `Booking ${bookingNumber} updated.${totalSuffix}`);
      await fetchBookings();
    } catch (err) {
      console.error('Update booking failed:', err);
      throw err;
    } finally {
      setSavingEdit(false);
    }
  }, [editingBooking, authSession, fetchBookings, showFlash]);

  // Default expand the tracking view for the first in-progress booking on load
  useEffect(() => {
    if (trackingOpen) return;
    const inProg = bookings.find((b) => isInProgress(b.status || firstTrip(b)?.tripStatus));
    if (inProg) setTrackingOpen(inProg.bookingId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  const counts = useMemo(() => {
    const c = { all: bookings.length, upcoming: 0, past: 0 };
    bookings.forEach((b) => {
      const k = bucket(b.status || firstTrip(b)?.tripStatus);
      if (k === 'upcoming') c.upcoming += 1;
      if (k === 'past') c.past += 1;
    });
    return c;
  }, [bookings]);

  const filtered = useMemo(
    () =>
      bookings.filter((b) => {
        if (filter === 'all') return true;
        return bucket(b.status || firstTrip(b)?.tripStatus) === filter;
      }),
    [bookings, filter]
  );

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50">
      <div className="px-6 py-6 max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">My bookings</h1>
          <p className="text-sm text-slate-500 mt-1">Track upcoming rides and review past trips.</p>
        </div>

        {flash && (
          <div
            role="status"
            className={cx(
              'rounded-lg px-4 py-2.5 text-sm flex items-center justify-between gap-3 fade-in',
              flash.type === 'success'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-rose-50 border border-rose-200 text-rose-800'
            )}
          >
            <span className="flex items-center gap-2">
              {flash.type === 'success' && <IconCircleCheck size={14} />}
              {flash.message}
            </span>
            <button
              onClick={() => setFlash(null)}
              className="text-current opacity-60 hover:opacity-100 cursor-pointer"
              aria-label="Dismiss"
            >
              <IconX size={14} />
            </button>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Pill active={filter === 'all'} onClick={() => setFilter('all')} count={counts.all}>All</Pill>
          <Pill active={filter === 'upcoming'} onClick={() => setFilter('upcoming')} count={counts.upcoming}>
            Upcoming
          </Pill>
          <Pill active={filter === 'past'} onClick={() => setFilter('past')} count={counts.past}>Past</Pill>
        </div>

        <div>
        {loading ? (
          <Card className="p-10 text-center text-sm text-slate-500">Loading your rides…</Card>
        ) : error ? (
          <Card className="p-6 text-center">
            <div className="text-sm text-rose-700 mb-3">{error}</div>
            <Button variant="outline" size="sm" onClick={fetchBookings}>
              Retry
            </Button>
          </Card>
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-3">
              <IconCar size={20} />
            </div>
            <div className="text-sm font-semibold text-slate-800">
              {bookings.length === 0 ? 'No rides yet' : 'Nothing in this view'}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {bookings.length === 0
                ? 'Your bookings will appear here once you book a ride.'
                : 'Switch filters to see other bookings.'}
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((b) => (
              <BookingRow
                key={b.bookingId || b.bookingNumber}
                booking={b}
                trackingOpen={trackingOpen}
                setTrackingOpen={setTrackingOpen}
                menuOpen={menuOpen}
                setMenuOpen={setMenuOpen}
                authSession={authSession}
                onCancelBooking={cancelBooking}
                cancelling={cancellingId === b.bookingId}
                onEditBooking={openEdit}
              />
            ))}
          </div>
        )}
        </div>
      </div>

      {editingBooking && (
        <EditBookingModal
          booking={editingBooking}
          onClose={closeEdit}
          onSave={saveEdit}
          saving={savingEdit}
        />
      )}
    </div>
  );
}

export default MyRidesPage;
